import React from "react";
import { CubeTransparentIcon, PencilIcon } from "@heroicons/react/24/outline";
import { CopyableText } from "@via/components/copyable-text/copyable-text.tsx";
import { LabeledLink } from "@via/components/labeled-link/labeled-link.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { WORKFLOW_STATUS } from "@via/utils/constants.tsx";

const statusStyles: Record<WORKFLOW_STATUS, string> = {
  [WORKFLOW_STATUS.RELEASED]:
    "bg-green-100 border border-green-200 text-green-800",
  [WORKFLOW_STATUS.DEVELOPMENT]:
    "bg-yellow-100 border border-yellow-200 text-yellow-800",
  [WORKFLOW_STATUS.ARCHIVED]:
    "bg-gray-200 border border-gray-300 text-gray-700",
};

const StatusBadge: React.FC<{ status: string | undefined }> = ({ status }) => {
  if (
    !status ||
    !Object.values(WORKFLOW_STATUS).includes(status as WORKFLOW_STATUS)
  ) {
    return <span>-</span>;
  }

  return (
    <span
      className={`inline-block rounded-2xl px-3 py-1 first-letter:capitalize text-sm font-semibold ${statusStyles[status]}`}>
      {status}
    </span>
  );
};

type Link = { label: string; url: string };

const isLinkArray = (value: unknown): value is Link[] => {
  return (
    Array.isArray(value) &&
    value.every(
      item =>
        typeof item === "object" &&
        item !== null &&
        "label" in item &&
        "url" in item
    )
  );
};

interface PipelineDetailsProps {
  onEditClick: () => void;
}

export const PipelineDetails: React.FC<PipelineDetailsProps> = ({
  onEditClick,
}) => {
  const { pipelineDraft, mainPipeline } = useGlobal();

  const status = pipelineDraft?.workflowStatus;
  const origin = pipelineDraft?.origin || "-";
  const tutorialLinksValue = pipelineDraft?.details?.tutorialLinks;
  const tutorialLinks = isLinkArray(tutorialLinksValue)
    ? tutorialLinksValue
    : [];
  const sourceLinksValue = pipelineDraft?.details?.sourceLinks;
  const sourceLinks = isLinkArray(sourceLinksValue) ? sourceLinksValue : [];
  const exampleRunLinksValue = pipelineDraft?.details?.exampleRunLinks;
  const exampleRunLinks = isLinkArray(exampleRunLinksValue)
    ? exampleRunLinksValue
    : [];
  const pipelineRevUUId = pipelineDraft?.pipelineRevUUId;
  const shareLinkWithRevUUId = window.location.pathname.includes("/beta")
    ? `${window.location.origin}/beta/pipelines/${pipelineRevUUId}`
    : `${window.location.origin}/pipelines/${pipelineRevUUId}`;

  return (
    <div className="w-full space-y-4 min-h-[500px] p-6 ring-1 ring-gray-300 rounded-lg">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <CubeTransparentIcon className="h-5 w-5" />
          <h2 className="via-h2">Pipeline Details</h2>
        </div>

        <button
          data-testid="edit-pipeline-details"
          onClick={onEditClick}
          className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-600"
          title="Edit pipeline details">
          <PencilIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Status</h2>
        <StatusBadge status={status} />
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Origin</h2>
        <div className="mt-2 text-sm font-semibold">{origin}</div>
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Tutorial & Docs</h2>
        {tutorialLinks.length > 0 ? (
          <div className="flex flex-col gap-2">
            {tutorialLinks.map((link, index) => (
              <LabeledLink key={index} label={link.label} url={link.url} />
            ))}
          </div>
        ) : (
          <span>-</span>
        )}
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Source Code</h2>
        {sourceLinks.length > 0 ? (
          <div className="flex flex-col gap-2">
            {sourceLinks.map((link, index) => (
              <LabeledLink key={index} label={link.label} url={link.url} />
            ))}
          </div>
        ) : (
          <span>-</span>
        )}
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Example Runs</h2>
        {exampleRunLinks.length > 0 ? (
          <div className="flex flex-col gap-2">
            {exampleRunLinks.map((link, index) => (
              <LabeledLink key={index} label={link.label} url={link.url} />
            ))}
          </div>
        ) : (
          <span>-</span>
        )}
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Pipeline UUID</h2>
        <CopyableText value={pipelineRevUUId as string} isTruncated />
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Share Link</h2>
        <CopyableText value={shareLinkWithRevUUId} isTruncated />
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Created By</h2>
        <div className="mt-2 text-sm font-semibold">
          {mainPipeline.username}
        </div>
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Created At</h2>
        <div className="mt-2 text-sm font-semibold">
          {formatDateString(mainPipeline.date_created, "LLL")}
        </div>
      </div>

      <div>
        <h2 className="via-h2 text-gray-500 mb-2">Last Updated At</h2>
        <div className="mt-2 text-sm font-semibold">
          {formatDateString(mainPipeline.date_modified, "LLL")}
        </div>
      </div>
    </div>
  );
};
